import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { toast } from 'sonner';
import { TrendingUp, Video, Scale, Upload, Check, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function WeightTracking() {
  const { cattle, addWeightRecord } = useCattle();
  const [selectedMethod, setSelectedMethod] = useState('scale');
  const [videoFile, setVideoFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [estimatedWeight, setEstimatedWeight] = useState(null);
  
  const [formData, setFormData] = useState({
    cattleId: '',
    weight: '',
    date: new Date().toISOString().split('T')[0],
    method: 'Báscula',
    notes: ''
  });

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      toast.success('Video cargado correctamente');
    }
  };

  const analyzeVideo = () => {
    if (!videoFile) {
      toast.error('Por favor suba un video primero');
      return;
    }

    setIsAnalyzing(true);
    
    setTimeout(() => {
      const mockWeight = Math.floor(Math.random() * (450 - 350 + 1)) + 350;
      setEstimatedWeight(mockWeight);
      setFormData(prev => ({ ...prev, weight: mockWeight.toString(), method: 'Video' }));
      setIsAnalyzing(false);
      toast.success('Análisis completado', {
        description: `Peso estimado: ${mockWeight} kg`
      });
    }, 2500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cattleId || !formData.weight) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    const selectedCattle = cattle.find(c => c.id === formData.cattleId);
    addWeightRecord({
      ...formData,
      weight: parseFloat(formData.weight)
    });
    
    toast.success('Peso registrado exitosamente', {
      description: `${selectedCattle?.name}: ${formData.weight} kg`
    });
    
    setFormData({
      cattleId: '',
      weight: '',
      date: new Date().toISOString().split('T')[0],
      method: selectedMethod === 'scale' ? 'Báscula' : 'Video',
      notes: ''
    });
    setVideoFile(null);
    setEstimatedWeight(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-md">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Seguimiento de Peso</h1>
            <p className="text-muted-foreground">Registre el peso por báscula o análisis de video</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle>Registro de Peso</CardTitle>
            <CardDescription>Seleccione el método de medición</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedMethod} onValueChange={setSelectedMethod} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="scale" className="flex items-center gap-2">
                  <Scale className="h-4 w-4" />
                  Báscula
                </TabsTrigger>
                <TabsTrigger value="video" className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Video IA
                </TabsTrigger>
              </TabsList>

              <TabsContent value="scale">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="cattle-select">Seleccione Ganado *</Label>
                    <Select 
                      value={formData.cattleId} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, cattleId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un animal" />
                      </SelectTrigger>
                      <SelectContent>
                        {cattle.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.tag} - {c.name} ({c.breed})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight">Peso (kg) *</Label>
                      <Input
                        id="weight"
                        type="number"
                        step="0.1"
                        placeholder="380.5"
                        value={formData.weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">Fecha</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      placeholder="Observaciones sobre la medición..."
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full shadow-lg hover:shadow-xl transition-all">
                    <Check className="mr-2 h-5 w-5" />
                    Registrar Peso
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="video">
                <div className="space-y-6">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Suba un video del ganado en posición lateral. Nuestro sistema de IA analizará el video para estimar el peso.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="cattle-select-video">Seleccione Ganado *</Label>
                    <Select 
                      value={formData.cattleId} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, cattleId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un animal" />
                      </SelectTrigger>
                      <SelectContent>
                        {cattle.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.tag} - {c.name} ({c.breed})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-rose-400 transition-colors">
                    <input
                      type="file"
                      id="video-upload"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      {videoFile ? (
                        <div className="space-y-2">
                          <Video className="h-16 w-16 mx-auto text-rose-600" />
                          <p className="font-medium">{videoFile.name}</p>
                          <p className="text-sm text-muted-foreground">Video cargado - Listo para analizar</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-16 w-16 mx-auto text-muted-foreground" />
                          <p className="text-sm font-medium">Click para subir video</p>
                          <p className="text-xs text-muted-foreground">MP4, MOV, AVI hasta 100MB</p>
                        </div>
                      )}
                    </label>
                  </div>

                  {videoFile && (
                    <Button 
                      onClick={analyzeVideo} 
                      disabled={isAnalyzing}
                      size="lg" 
                      className="w-full shadow-lg hover:shadow-xl transition-all"
                    >
                      {isAnalyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Analizando video...
                        </>
                      ) : (
                        <>
                          <Video className="mr-2 h-5 w-5" />
                          Analizar con IA
                        </>
                      )}
                    </Button>
                  )}

                  {estimatedWeight && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 text-center space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Peso Estimado</p>
                        <p className="text-4xl font-bold text-rose-600">{estimatedWeight} kg</p>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="date-video">Fecha</Label>
                            <Input
                              id="date-video"
                              type="date"
                              value={formData.date}
                              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="notes-video">Notas</Label>
                            <Textarea
                              id="notes-video"
                              placeholder="Observaciones..."
                              value={formData.notes}
                              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                              rows={2}
                            />
                          </div>
                          <Button type="submit" size="lg" className="w-full">
                            <Check className="mr-2 h-5 w-5" />
                            Confirmar y Registrar
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border/50 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Método de Báscula</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="mt-0.5">
                  <Check className="h-3 w-3 mr-1" />
                  Preciso
                </Badge>
                <p className="text-sm text-muted-foreground">Medición exacta del peso real</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="mt-0.5">
                  <Scale className="h-3 w-3 mr-1" />
                  Manual
                </Badge>
                <p className="text-sm text-muted-foreground">Requiere llevar el ganado a la báscula</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 shadow-sm bg-gradient-to-br from-rose-50/50 to-rose-100/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Video className="h-5 w-5 text-rose-600" />
                Método de Video IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="mt-0.5 border-rose-300">
                  <Video className="h-3 w-3 mr-1" />
                  Rápido
                </Badge>
                <p className="text-sm text-muted-foreground">Estimación instantánea sin estrés</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="mt-0.5 border-rose-300">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  IA
                </Badge>
                <p className="text-sm text-muted-foreground">Análisis por inteligencia artificial</p>
              </div>
              <Alert className="mt-4">
                <AlertDescription className="text-xs">
                  <strong>Nota:</strong> Esta es una función de prototipo que simula el análisis de IA para estimar peso del ganado basado en video.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
